import React, {
  FocusEventHandler,
  SyntheticEvent,
  useEffect,
  useState,
  CSSProperties,
} from "react";

import {
  Tooltip,
  Typography,
  Checkbox,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  colors,
  fontSize,
  fontWeight,
  lineHeight,
  // padding,
} from "../../../theme/defaultStyle";
import { styled } from "@mui/material/styles";

export type OptionRenderType = {
  text: string;
  value: string;
};

export type StewartAutocompleteEventType = {
  target: { name: string; value: OptionRenderType[] };
};

type StewartMultiSelectProps = {
  placeholder?: string;
  name: string;
  options: any[];
  optionsTextField?: string;
  optionsValueField?: string;
  disabled?: boolean;
  clearSelectionOnDisable?: boolean;
  onChange?: (
    e: StewartAutocompleteEventType,
    value: OptionRenderType[],
    selectedOption?: OptionRenderType
  ) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  controlStyle?: CSSProperties;
  readOnly?: boolean;
  hasUntouchedValidation?: boolean;
  required?: boolean;
  freeSolo?: boolean;
  defaultSelectedOption?: OptionRenderType | OptionRenderType[];
  getOptionDisabled?: (option: OptionRenderType) => boolean;
  onOpen?: (event?: SyntheticEvent) => void;
  onClose?: (event?: SyntheticEvent) => void;
  textFieldRef?: React.RefObject<HTMLInputElement>;
  hasErrorIn?: boolean;
  inputClassName?: string;
  errorMessage?: string;
  excludeFromDisplayValue?: string[];
  disableAutoSelectSingle?: boolean;
  shouldSetDefaultSelectedValue?: boolean;
};

const CustomTextField = styled(TextField)(() => ({
  "& .MuiInputBase-input.Mui-disabled": {
    backgroundColor: colors.white01,
  },
  "& input::placeholder": {
    color: `${colors.white01} !important`,
  },
}));

const StyledAutocomplete = styled(Autocomplete)(() => ({
  // "& .MuiInputBase-root": { height: "40px", padding: `${padding.zero} ${padding.large1} !important` },
  "& .MuiOutlinedInput-root": {
    flexWrap: "nowrap !important",
    "&.Mui-focused fieldset": {
      border: `2px solid ${colors.blue01} !important`,
    },
    "&.Mui-disabled": {
      backgroundColor: colors.white01,
    },
    // "& .MuiOutlinedInput-input": {
    //   padding: `${padding.zero} ${padding.xsmall2} !important`,
    //   height: "40px !important",
    // },
  },
  "& .MuiInputLabel-outlined": {
    "&.MuiInputLabel-shrink": {
      top: "2px !important",
    },
  },
  ".MuiSvgIcon-root": {
    fill: `${colors.blue01} !important`,
  },
}));

const StyledTypography = styled(Typography)(() => ({
  fontSize: `${fontSize.large} !important`,
  fontWeight: `${fontWeight.normal1} !important`,
  lineHeight: `${lineHeight.small1} !important`,
}));

const StewartMultiSelect = ({
  placeholder = "",
  name,
  optionsTextField = "text",
  optionsValueField = "value",
  options,
  onChange = () => {},
  onBlur = () => null,
  controlStyle = {},
  readOnly = false,
  disabled = false,
  clearSelectionOnDisable = false,
  hasUntouchedValidation = false,
  required = false,
  freeSolo = false,
  getOptionDisabled,
  onOpen,
  onClose,
  defaultSelectedOption,
  hasErrorIn,
  inputClassName = "",
  errorMessage = "",
  excludeFromDisplayValue,
  disableAutoSelectSingle = false,
  shouldSetDefaultSelectedValue = false,
}: StewartMultiSelectProps) => {
  const [fieldValue, setFieldValue] = useState<OptionRenderType[]>(() => {
    // Initialize with default value if provided
    if (defaultSelectedOption) {
      return Array.isArray(defaultSelectedOption) ? defaultSelectedOption : [defaultSelectedOption];
    }
    return [];
  });

  useEffect(() => {
    if (shouldSetDefaultSelectedValue) {
      if (defaultSelectedOption) {
        const newValue = Array.isArray(defaultSelectedOption)
          ? defaultSelectedOption
          : [defaultSelectedOption];
        setFieldValue(newValue);
      } else {
        setFieldValue([]);
      }
    }
  }, [defaultSelectedOption, shouldSetDefaultSelectedValue]);

  const optionsToRender: OptionRenderType[] = options
    .map((option) => ({
      text: option[optionsTextField],
      value: option[optionsValueField],
    }))
    .filter((option) => option.text);

  useEffect(() => {
    if (!disableAutoSelectSingle && options.length === 1 && fieldValue.length === 0) {
      const singleOption = {
        text: options[0][optionsTextField],
        value: options[0][optionsValueField],
      };
      setFieldValue([singleOption]);
      onChange(
        { target: { name: name.toString(), value: [singleOption] } },
        [singleOption]
      );
    }
  }, [options, disableAutoSelectSingle]);

  useEffect(() => {
    if (clearSelectionOnDisable && disabled && fieldValue.length > 0) {
      setFieldValue([]);
      onChange({ target: { name: name.toString(), value: [] } }, []);
    }
  }, [disabled, clearSelectionOnDisable, onChange, name, fieldValue.length]);

  const hasError =
    (fieldValue.length === 0 && hasUntouchedValidation && !defaultSelectedOption) || Boolean(errorMessage);

  const handleOnChange = (
    event: SyntheticEvent,
    value: OptionRenderType[],
    selectedOption?: OptionRenderType
  ) => {
    setFieldValue(value);
    onChange(
      { target: { name: name.toString(), value } }, value,
      selectedOption
    );
  };

  

  return (
    <StyledAutocomplete
      id={name.toString()}
      options={optionsToRender}
      value={fieldValue}
      onChange={(event, value, reason, details) => handleOnChange(event, value as OptionRenderType[], details?.option as OptionRenderType)}
      onBlur={onBlur}
      onOpen={onOpen}
      onClose={onClose}
      getOptionDisabled={(option) => Boolean(getOptionDisabled?.(option as OptionRenderType))}
      isOptionEqualToValue={(option, value) =>
        (option as OptionRenderType).value === (value as OptionRenderType).value
      }
      getOptionLabel={(option) => (option as OptionRenderType).text}
      renderTags={(value) => {
        const excludeValues = excludeFromDisplayValue || [];
        const filteredValue = (value as OptionRenderType[])
          .filter((elem) => !excludeValues.includes(elem.value))
          .filter(elem => elem.value);
        const displayText = filteredValue.map((elem) => elem.text).join(", ");

        return (

          <Tooltip
            title={displayText}
            placement="right"
            arrow
          >
            <StyledTypography noWrap>{displayText}</StyledTypography>
          </Tooltip>
        );
        }
      }
      renderOption={(props, option, { selected }) => (
        // @ts-ignore - Type conflict between @types/react versions, non-critical
        <li {...props}>
          <Checkbox
            sx={{ "& .MuiSvgIcon-root": { color: colors.blue01 } }}
            checked={selected}
          />
          {(option as OptionRenderType).text}
        </li>
      )}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          label={placeholder}
          // placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            // style: { padding: padding.zero },
          }}
          InputLabelProps={{
            shrink: Boolean(fieldValue.length) || Boolean(params.inputProps?.value),
          }}
          variant="outlined"
          required={required}
          error={hasError || hasErrorIn}
        />
      )}
      multiple
      disableCloseOnSelect
      disableClearable
      readOnly={readOnly}
      disabled={disabled}
      freeSolo={freeSolo}
      style={{ display: "flex", flexDirection: "column", width: "100%", ...controlStyle }}
      className={inputClassName}
    />
  );
};

export default StewartMultiSelect;
